/**
 * What makes an acceptable password.
 *
 * Shared deliberately. The server is the authority - it is the only
 * side an attacker cannot edit - but the child typing the box needs to
 * see the same five rules, ticking off as they are met, or they are
 * guessing against a wall. Two copies of that list would drift within a
 * month of somebody changing one of them.
 *
 * Plain JavaScript with no imports, so both the Node server and the
 * Vite client can read it without either dragging in the other's
 * dependencies.
 */

export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 200;

export const PASSWORD_RULES = [
  {
    id: 'length',
    label: `At least ${PASSWORD_MIN} characters`,
    message: `Password must be at least ${PASSWORD_MIN} characters`,
    test: (v) => v.length >= PASSWORD_MIN,
  },
  {
    id: 'upper',
    label: 'One uppercase letter (A–Z)',
    message: 'Password needs an uppercase letter',
    test: (v) => /[A-Z]/.test(v),
  },
  {
    id: 'lower',
    label: 'One lowercase letter (a–z)',
    message: 'Password needs a lowercase letter',
    test: (v) => /[a-z]/.test(v),
  },
  {
    id: 'digit',
    label: 'One number (0–9)',
    message: 'Password needs a number',
    test: (v) => /[0-9]/.test(v),
  },
  {
    /*
     * Anything that is not a letter or a digit counts. Defining it as a
     * list of allowed symbols instead is how people end up being told
     * their perfectly good password is invalid because they used a
     * character somebody forgot to write down.
     */
    id: 'special',
    label: 'One special character (! ? # @ …)',
    message: 'Password needs a special character, like ! ? # or @',
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

/** The rules this value does not yet satisfy. Empty means it is fine. */
export function failedRules(value = '') {
  const v = String(value ?? '');
  return PASSWORD_RULES.filter((rule) => !rule.test(v));
}

export const isStrongPassword = (value) => failedRules(value).length === 0;
